import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  Calculator,
  Camera,
  ChevronLeft,
  Clock,
  DollarSign,
  Eye,
  FileSignature,
  PhoneCall,
  FileText,
  ImageMinus,
  LogIn,
  LogOut,
  MapPin,
  MessageCircle,
  Play,
  Plus,
  Send,
  Timer,
  Trash2,
  Truck,
  Undo2,
  User2,
  Users,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { QuoteHistoryEntry, QuoteHistoryType } from '../../types/admin';

// ── Icon + color mapping ────────────────────────────────────────────────────

const ICON_MAP: Record<QuoteHistoryType, typeof Activity> = {
  created: Plus,
  status_change: ArrowRightLeft,
  lead_changed: User2,
  move_lead_changed: Truck,
  version_created: FileText,
  section_updated: Activity,
  formula_calculated: Calculator,
  date_changed: Clock,
  address_changed: MapPin,
  photos_added: Camera,
  customer_replied: MessageCircle,
  job_status_changed: ArrowRightLeft,
  stage_advanced: Play,
  stage_reverted: Undo2,
  signoff_captured: FileSignature,
  signoff_waived: FileSignature,
  crew_changed: Users,
  trucks_changed: Truck,
  outcome_changed: ArrowRightLeft,
  issue_logged: AlertTriangle,
  payment_request_sent: Send,
  payment_logged: DollarSign,
  hours_reconciled: Timer,
  deleted: Trash2,
  photos_removed: ImageMinus,
  truck_checkout: LogOut,
  truck_checkin: LogIn,
  portal_viewed: Eye,
  lead_activity_logged: PhoneCall,
};

const COLOR_MAP: Record<QuoteHistoryType, string> = {
  created: 'text-green-600 bg-green-50',
  status_change: 'text-blue-600 bg-blue-50',
  lead_changed: 'text-violet-600 bg-violet-50',
  move_lead_changed: 'text-violet-600 bg-violet-50',
  version_created: 'text-plum bg-plum/10',
  section_updated: 'text-slate bg-stone/50',
  formula_calculated: 'text-amber-600 bg-amber-50',
  date_changed: 'text-orange-600 bg-orange-50',
  address_changed: 'text-sky-600 bg-sky-50',
  photos_added: 'text-pink-600 bg-pink-50',
  customer_replied: 'text-emerald-600 bg-emerald-50',
  job_status_changed: 'text-indigo-600 bg-indigo-50',
  stage_advanced: 'text-blue-600 bg-blue-50',
  stage_reverted: 'text-amber-600 bg-amber-50',
  signoff_captured: 'text-green-600 bg-green-50',
  signoff_waived: 'text-amber-600 bg-amber-50',
  crew_changed: 'text-violet-600 bg-violet-50',
  trucks_changed: 'text-indigo-600 bg-indigo-50',
  outcome_changed: 'text-blue-600 bg-blue-50',
  issue_logged: 'text-rose bg-rose/10',
  payment_request_sent: 'text-plum bg-plum/10',
  payment_logged: 'text-green-600 bg-green-50',
  hours_reconciled: 'text-amber-600 bg-amber-50',
  deleted: 'text-rose bg-rose/10',
  photos_removed: 'text-pink-600 bg-pink-50',
  truck_checkout: 'text-indigo-600 bg-indigo-50',
  truck_checkin: 'text-indigo-600 bg-indigo-50',
  portal_viewed: 'text-emerald-600 bg-emerald-50',
  lead_activity_logged: 'text-teal-600 bg-teal-50',
};

// ── Relative time ───────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fullDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Page ────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export function QuoteHistoryPage() {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading: quoteLoading } = useSharedQuote();

  const [entries, setEntries] = useState<QuoteHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchHistory = useCallback(async (fetchLimit: number) => {
    if (!orgId || !quote?.id) return;
    try {
      const res = await api.get<{ success: boolean; data: QuoteHistoryEntry[] }>(
        `/quotes/${orgId}/${quote.id}/history?limit=${fetchLimit}`,
      );
      const data = res.data ?? [];
      setEntries(data);
      // If we got exactly the limit, there might be more
      setHasMore(data.length >= fetchLimit);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [orgId, quote?.id]);

  useEffect(() => { fetchHistory(limit); }, [fetchHistory, limit]);

  function handleShowMore() {
    setLoadingMore(true);
    setLimit(prev => prev + PAGE_SIZE);
  }

  if (quoteLoading || loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-8">
      {/* Back — mobile only */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quote.id}`)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1 md:hidden"
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div className="flex items-center gap-2 mb-5">
        <Activity size={20} className="text-plum" />
        <h1 className="font-display text-h2 text-midnight">History</h1>
        {entries.length > 0 && (
          <span className="text-[12px] text-slate bg-stone/80 px-1.5 py-0.5 rounded-full tabular-nums">
            {entries.length}{hasMore ? '+' : ''}
          </span>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-8 text-center">
          <Activity size={32} className="text-slate/30 mx-auto mb-3" />
          <p className="text-[14px] text-slate">No history recorded yet</p>
        </div>
      ) : (
        <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
          <div className="divide-y divide-stone">
            {entries.map((entry) => {
              const Icon = ICON_MAP[entry.type] ?? Activity;
              const color = COLOR_MAP[entry.type] ?? 'text-slate bg-stone/50';
              return (
                <div key={entry.id} className="flex items-start gap-3 px-4 py-3">
                  <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${color}`}>
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-midnight leading-snug">
                      {entry.description}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate">
                        {entry.userDisplayName}
                      </span>
                      <span className="text-[11px] text-slate/50">&middot;</span>
                      <span className="text-[11px] text-slate" title={fullDate(entry.timestamp)}>
                        {relativeTime(entry.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={handleShowMore}
              disabled={loadingMore}
              className="w-full py-3 text-[13px] font-semibold text-plum hover:bg-stone/30
                         transition-colors border-t border-stone disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
